/**
 * Sanitizes error messages to provide user-friendly, secure feedback
 * Removes technical details and provides consistent messaging
 */

// Common error patterns and their user-friendly messages
const ERROR_PATTERNS = {
  // RLS Policy violations
  42501: "Access denied: You do not have permission to perform this action.",
  policy: "Access denied: You do not have permission to perform this action.",

  // Payout locking specific messages (these are safe to show as-is)
  "payouts have been locked": "payouts have been locked",
  "cannot be modified": "cannot be modified",

  // Database constraint violations
  "foreign key":
    "This operation cannot be completed because it would affect related data.",
  "unique constraint": "This item already exists with the same information.",
  "not null": "Required information is missing.",

  // Network/connection errors
  network:
    "Connection error. Please check your internet connection and try again.",
  timeout: "Request timed out. Please try again.",
  fetch: "Unable to connect to the server. Please try again.",

  // Authentication errors
  auth: "Authentication error. Please log in again.",
  unauthorized: "You are not authorized to perform this action.",
  forbidden: "Access denied.",

  // Generic database errors
  database: "Database error. Please try again.",
  sql: "Database error. Please try again.",

  // File/upload errors
  file: "File error. Please try again.",
  upload: "Upload failed. Please try again.",
  size: "File is too large.",

  // Validation errors
  validation: "Please check your input and try again.",
  invalid: "Invalid information provided.",
};

/**
 * Sanitizes an error message for user display
 * @param {Error|string} error - The error object or message
 * @param {string} context - Context about what operation failed
 * @param {boolean} isPayoutFinalized - Whether payouts are locked
 * @returns {string} Sanitized, user-friendly error message
 */
export const sanitizeErrorMessage = (
  error,
  context = "operation",
  isPayoutFinalized = false
) => {
  // Handle different error input types
  let errorMessage = "";
  let errorCode = "";

  if (typeof error === "string") {
    errorMessage = error;
  } else if (error && typeof error === "object") {
    errorMessage = error.message || error.toString();
    errorCode = error.code || "";
  }

  // Check for payout locking first (these are safe and informative)
  if (
    isPayoutFinalized &&
    errorMessage.toLowerCase().includes("payouts have been locked")
  ) {
    return `Cannot ${context}: Payouts have been locked and cannot be modified.`;
  }

  // Check for specific error codes
  if (errorCode === "42501") {
    if (isPayoutFinalized) {
      return `Cannot ${context}: Payouts have been locked and cannot be modified.`;
    }
    return `Access denied: You do not have permission to ${context}.`;
  }

  // Check for policy-related errors
  if (errorMessage.toLowerCase().includes("policy")) {
    if (isPayoutFinalized) {
      return `Cannot ${context}: Payouts have been locked and cannot be modified.`;
    }
    return `Access denied: You do not have permission to ${context}.`;
  }

  // Check for other known error patterns
  for (const [pattern, userMessage] of Object.entries(ERROR_PATTERNS)) {
    if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
      return userMessage;
    }
  }

  // For unknown errors, provide a generic but helpful message
  return `Unable to ${context}. Please try again or contact support if the problem persists.`;
};

/**
 * Sanitizes error messages for specific operations
 */
export const sanitizeOperationError = {
  create: (error, itemType, isPayoutFinalized) =>
    sanitizeErrorMessage(error, `create ${itemType}`, isPayoutFinalized),

  update: (error, itemType, isPayoutFinalized) =>
    sanitizeErrorMessage(error, `update ${itemType}`, isPayoutFinalized),

  delete: (error, itemType, isPayoutFinalized) =>
    sanitizeErrorMessage(error, `delete ${itemType}`, isPayoutFinalized),

  read: (error, itemType) => sanitizeErrorMessage(error, `load ${itemType}`),

  save: (error, itemType, isPayoutFinalized) =>
    sanitizeErrorMessage(error, `save ${itemType}`, isPayoutFinalized),
};

/**
 * Logs the full error details for debugging while showing sanitized message to user
 * @param {Error|string} error - The original error
 * @param {string} operation - What operation was being performed
 * @param {string} sanitizedMessage - The sanitized message shown to user
 */
export const logErrorForDebugging = (error, operation, sanitizedMessage) => {
  // Removed console logging to clean up console output
  // Error details are still available in the error object for debugging if needed
};
